import { partsInPersonTimezone } from '../../i18n/datetime';

/**
 * Janela de leitura da presenca: do inicio do mes anterior ao inicio do mes
 * seguinte ao proximo, sempre nas viradas de mes **do fuso da pessoa**.
 *
 * `timezone` e parametro obrigatorio de proposito. A versao anterior montava a
 * janela com `new Date().getMonth()`, que le o relogio e o fuso do navegador:
 * quem da aula em Nova York e abre a tela de Sao Paulo as 22h de 31 de janeiro
 * pedia fevereiro e perdia a aula que, para ele, ainda era de janeiro. A §2.2
 * manda exibir no fuso IANA da pessoa, e um parametro opcional com default
 * deixaria o erro voltar pela porta do default.
 */
export interface AttendanceWindow {
  readonly from: string;
  readonly to: string;
}

const MONTHS_BEFORE = 1;
const MONTHS_AFTER = 2;

export function attendanceWindow(now: Date, timezone: string): AttendanceWindow {
  const parts = partsInPersonTimezone(now, timezone);
  const year = Number(parts.year);
  const month = Number(parts.month) - 1; // `Date` conta o mes a partir de zero

  return {
    from: startOfMonth(year, month - MONTHS_BEFORE, timezone),
    to: startOfMonth(year, month + MONTHS_AFTER, timezone),
  };
}

/**
 * Instante UTC que, lido no fuso pedido, e o dia 1 daquele mes as 00:00.
 *
 * Duas passadas, e nao uma: a primeira usa o deslocamento vigente no palpite
 * ingenuo, a segunda o corrige quando o palpite caiu do outro lado de uma
 * virada de horario de verao. Sem a segunda a borda erraria em uma hora nos
 * dois dias do ano em que o fuso muda — pouco, mas o bastante para a aula da
 * meia-noite entrar ou sair da lista.
 */
function startOfMonth(year: number, month: number, timezone: string): string {
  const wallClock = Date.UTC(year, month, 1);
  const guess = wallClock - offsetAt(wallClock, timezone);
  return new Date(wallClock - offsetAt(guess, timezone)).toISOString();
}

/** Quanto o relogio do fuso adianta o de UTC naquele instante, em milissegundos. */
function offsetAt(instant: number, timezone: string): number {
  // `partsInPersonTimezone` para no minuto; truncar o instante junto impede que
  // o segundo quebrado entre na conta como se fosse deslocamento.
  const minute = Math.floor(instant / 60_000) * 60_000;
  const parts = partsInPersonTimezone(new Date(minute), timezone);
  const asIfUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
  );
  return asIfUTC - minute;
}
